/**
 * MigrationProgressSidebar
 *
 * Left-side journey-progress sidebar for the PagerDuty Migration Assistant.
 * Visually mirrors the cleanup sidebar's collapsed/expanded modes and
 * timeline rendering, but reads from ``useMigrationProgress`` and uses
 * migration-specific icons + labels.
 *
 * Behaviour parity with cleanup-progress-sidebar.tsx:
 *   - Collapsed (mobile / user-toggled): only the icons + percent badge.
 *   - Expanded: timeline with display_name labels, badge, recent threads.
 *   - Recent threads section uses the shared useThreads hook so navigation
 *     and "New Migration" work identically.
 *
 * Icons are keyed by ``step_name`` to a Lucide icon picked to match the
 * journey semantics (search → list → play → file).
 */

import React, { ComponentType, useEffect } from 'react';
import {
  Box,
  Divider,
  Flex,
  Heading,
  Text,
  Tooltip,
  VStack,
  Badge,
  useBreakpointValue,
} from '@chakra-ui/react';
import {
  Search,
  ClipboardList,
  Play,
  FileText,
  Circle,
  MessageSquarePlus,
  MessageSquare,
  LucideProps,
} from 'lucide-react';

import {
  useMigrationProgress,
  MigrationStep,
} from '@/providers/migration-progress-provider';
import { useThreads } from '@/hooks/use-threads';

const PAGERDUTY_GREEN = '#06AC38';
const PAGERDUTY_BLUE = '#0052cc';
const GRAY_DEFAULT = '#A0AEC0';

type IconCmp = ComponentType<LucideProps>;

// Per-step Lucide icon. Keep the dict shallow so adding a step is trivial.
const STEP_ICONS: Record<string, IconCmp> = {
  discover: Search,
  plan: ClipboardList,
  execute: Play,
  report: FileText,
};

function getMigrationStepIcon(stepName: string): IconCmp {
  return STEP_ICONS[stepName] || Circle;
}

interface MigrationProgressSidebarProps {
  isCollapsed: boolean;
  onToggle: () => void;
}

export const MigrationProgressSidebar: React.FC<
  MigrationProgressSidebarProps
> = ({ isCollapsed, onToggle }) => {
  // NOTE: per-resource-type detail no longer lives in the sidebar — it
  // moved to ``MigrationProgressModal`` (auto-opens during execution) +
  // ``MigrationProgressPill`` (floating reopen affordance). The sidebar
  // now only shows the 4 macro journey steps.
  const { steps, currentStep, progressPercent } = useMigrationProgress();
  const {
    threads,
    currentThreadId,
    switchThread,
    fetchRecentThreads,
    newChat,
  } = useThreads();

  useEffect(() => {
    fetchRecentThreads();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const formatRelativeTime = (ts: number) => {
    const days = Math.floor((Date.now() - ts) / 86400000);
    if (days <= 0) return 'Today';
    if (days === 1) return 'Yesterday';
    if (days < 7) return `${days}d ago`;
    return new Date(ts).toLocaleDateString();
  };

  // Force collapsed on mobile, respect prop on desktop
  const isMobile = useBreakpointValue({ base: true, md: false });
  const forceCollapsed = isMobile ? true : isCollapsed;

  const renderStep = (step: MigrationStep, isLast: boolean, expanded: boolean) => {
    const isCurrentStep = currentStep?.step_name === step.step_name;
    const isCompleted = step.status === 'Complete';
    const StepIcon = getMigrationStepIcon(step.step_name);

    const iconColor = isCompleted
      ? PAGERDUTY_GREEN
      : isCurrentStep
        ? PAGERDUTY_BLUE
        : GRAY_DEFAULT;

    if (!expanded) {
      return (
        <Box key={step.step_name} position="relative">
          <Box py={4}>
            <StepIcon size={20} color={iconColor} />
          </Box>
          {!isLast && (
            <Box
              position="absolute"
              left="50%"
              top="28px"
              my={4}
              w="2px"
              h="16px"
              bg={isCompleted ? PAGERDUTY_GREEN : 'gray.200'}
              transform="translateX(-50%)"
            />
          )}
        </Box>
      );
    }

    return (
      <Box key={step.step_name} position="relative">
        <Flex align="center" gap={3} py={2}>
          <Box position="relative" py={2} flexShrink={0}>
            <StepIcon size={20} color={iconColor} />
          </Box>
          <Box flex={1}>
            <Text
              fontSize="sm"
              fontWeight={isCurrentStep ? 'semibold' : 'normal'}
              color={
                isCompleted || isCurrentStep ? 'gray.800' : 'gray.500'
              }
            >
              {step.display_name || step.step_name}
            </Text>
            {isCurrentStep && (
              <Text fontSize="xs" color={PAGERDUTY_BLUE}>
                In progress…
              </Text>
            )}
          </Box>
        </Flex>

        {!isLast && (
          <Box
            position="absolute"
            left="10px"
            top="28px"
            my={4}
            w="2px"
            h="16px"
            bg={isCompleted ? PAGERDUTY_GREEN : 'gray.200'}
            transform="translateX(-50%)"
          />
        )}
      </Box>
    );
  };

  return (
    <Box
      w={{ base: '50px', md: forceCollapsed ? '60px' : '280px' }}
      minW={{ base: '50px', md: '100px' }}
      h="100%"
      bg="white"
      borderRight="1px solid"
      borderColor="gray.200"
      position="relative"
      transition="all 0.3s ease-in-out"
      display="flex"
      flexDirection="column"
    >
      <Box
        flex="1"
        overflowY="auto"
        overflowX="hidden"
        p={{ base: 2, md: forceCollapsed ? 2 : 6 }}
      >
        {forceCollapsed ? (
          // ---------------- Collapsed ----------------
          <VStack spacing={0} align="center" pt={4}>
            <Badge
              colorScheme={progressPercent === 100 ? 'green' : 'blue'}
              fontSize="sm"
              fontWeight="bold"
              borderRadius="full"
              px={4}
              py={1}
              minW="44px"
              textAlign="center"
              mb={4}
            >
              {progressPercent}%
            </Badge>

            <VStack spacing={0} align="center">
              {steps.map((step, idx) =>
                renderStep(step, idx === steps.length - 1, false),
              )}
            </VStack>

            <Divider my={3} borderColor="gray.200" />
            <Tooltip label="New Migration" placement="right">
              <Box
                as="button"
                aria-label="New Migration"
                onClick={newChat}
                w="36px"
                h="36px"
                borderRadius="md"
                display="flex"
                alignItems="center"
                justifyContent="center"
                _hover={{ bg: 'gray.100' }}
              >
                <MessageSquarePlus size={18} color={PAGERDUTY_BLUE} />
              </Box>
            </Tooltip>
          </VStack>
        ) : (
          // ---------------- Expanded ----------------
          <>
            <Heading size="md" mb={4}>
              Migration Progress
            </Heading>

            <Box mb={4}>
              <Flex justify="space-between" align="center" mb={2}>
                <Badge
                  colorScheme={progressPercent === 100 ? 'green' : 'blue'}
                  fontSize="sm"
                  fontWeight="bold"
                  borderRadius="full"
                  px={6}
                  py={1}
                  minW="44px"
                  textAlign="center"
                >
                  {progressPercent}% Complete
                </Badge>
              </Flex>
            </Box>

            <VStack align="stretch" spacing={0}>
              {steps.map((step, idx) =>
                renderStep(step, idx === steps.length - 1, true),
              )}
            </VStack>

            <Divider my={4} borderColor="gray.200" />

            <Flex justify="space-between" align="center" mb={3}>
              <Flex align="center">
                <MessageSquare size={13} color={GRAY_DEFAULT} />
                <Text
                  ml={2}
                  fontSize="xs"
                  fontWeight="semibold"
                  color="gray.500"
                  textTransform="uppercase"
                  letterSpacing="wide"
                >
                  Recent
                </Text>
              </Flex>
              <Box
                as="button"
                display="flex"
                alignItems="center"
                gap={1}
                px={2}
                py={1}
                borderRadius="md"
                fontSize="xs"
                color={PAGERDUTY_BLUE}
                fontWeight="medium"
                _hover={{ bg: 'blue.50' }}
                onClick={newChat}
              >
                <MessageSquarePlus size={12} />
                New Migration
              </Box>
            </Flex>

            {threads.length === 0 ? (
              <Text fontSize="xs" color="gray.400" px={2}>
                No previous conversations
              </Text>
            ) : (
              <VStack align="stretch" spacing={0}>
                {threads.slice(0, 5).map((thread) => {
                  const isActive =
                    thread.langGraphThreadId === currentThreadId;
                  return (
                    <Box
                      key={thread.langGraphThreadId}
                      as="button"
                      textAlign="left"
                      w="full"
                      px={2}
                      py={2}
                      borderRadius="md"
                      bg={isActive ? 'blue.50' : 'transparent'}
                      _hover={{
                        bg: isActive ? 'blue.50' : 'gray.50',
                      }}
                      onClick={() =>
                        switchThread(thread.langGraphThreadId)
                      }
                    >
                      <Flex align="center" gap={2}>
                        <Box flex={1} minW={0}>
                          <Text
                            fontSize="sm"
                            noOfLines={1}
                            color={isActive ? 'blue.700' : 'gray.700'}
                            fontWeight={isActive ? 'medium' : 'normal'}
                          >
                            {thread.title ?? 'Untitled'}
                          </Text>
                          <Text fontSize="xs" color="gray.400">
                            {formatRelativeTime(thread.updatedAt)}
                          </Text>
                        </Box>
                      </Flex>
                    </Box>
                  );
                })}
              </VStack>
            )}
          </>
        )}
      </Box>

      {/* Toggle Button - Hidden on mobile */}
      <Box
        position="absolute"
        top="50%"
        right={0}
        transform="translateY(-50%)"
        zIndex={10}
        display={{ base: 'none', md: 'block' }}
      >
        <Box
          as="button"
          aria-label={isCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          onClick={onToggle}
          w="24px"
          h="60px"
          bg="white"
          borderRadius="8px 0 0 8px"
          cursor="pointer"
          display="flex"
          alignItems="center"
          justifyContent="center"
          _hover={{ bg: 'gray.50' }}
          _active={{ bg: 'gray.100' }}
          transition="all 0.2s"
        >
          <Box w="2px" h="24px" bg="gray.400" borderRadius="full" />
        </Box>
      </Box>
    </Box>
  );
};

export default MigrationProgressSidebar;
